// Records Retention — monitoring + manual disposal review. NO auto-delete; actions only record a decision
// in retention_reviews. Admin/QM act; other office roles view; Auditor logins are excluded (nav + view).
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO } from 'date-fns'
import { failed } from '../lib/toast'

// Standard (deletable) records vs immutable evidence-chain records (monitor-only wording).
const ACTIONS_STD = [
  ['Disposal Approved', 'Approve disposal'],
  ['Disposal Deferred', 'Defer'],
  ['Disposed / Archived', 'Mark disposed'],
]
const ACTIONS_IMMUTABLE = [
  ['Reviewed - Retain Permanently', 'Retain permanently'],
  ['Retention Reviewed - No Disposal', 'No disposal'],
]

function statusBadge(s) {
  if (s === 'Eligible for Review') return 'badge-warn'
  if (s === 'Due within 90 days') return 'badge-info'
  if (s === 'Disposed / Archived' || s === 'Disposal Approved' || s === 'Reviewed - Retain Permanently' || s === 'Retention Reviewed - No Disposal') return 'badge-ok'
  if (s === 'Disposal Deferred') return 'badge-warn'
  return 'badge'
}

export default function RecordsRetention() {
  const { profile, canApprove, isAuditor, logAction } = useAuth()   // canApprove = QM or Admin
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')   // all (≤90d) | eligible | due90
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (!isAuditor) load() }, [isAuditor])
  async function load() {
    setLoading(true)
    // Only fetch records that are eligible now (days_until ≤ 0) or due within 90 days — keeps the list lean.
    const { data, error } = await supabase.from('retention_overview')
      .select('*').lte('days_until', 90).order('days_until', { ascending: true })
    if (error) console.warn('retention_overview load error (run RECORDS_RETENTION.sql):', error.message)
    setRows(data || []); setLoading(false)
  }
  function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }
  const statusOf = r => r.review_status || r.base_status

  async function record(r, status, label) {
    const reason = window.prompt(`${label}\n\n${r.reference_label || ''}\n\nReason / remarks (optional):`, r.reason || '')
    if (reason === null) return   // cancelled
    setBusy(true)
    const payload = {
      record_category: r.record_category, source_table: r.source_table, source_id: r.source_id,
      reference_label: r.reference_label, retention_start_date: r.retention_start_date, retention_until: r.retention_until,
      disposal_status: status, reviewed_by: profile?.name || 'Staff', reviewed_at: new Date().toISOString(),
      review_decision: label, reason: reason || null, updated_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('retention_reviews').upsert(payload, { onConflict: 'source_table,source_id' })
    setBusy(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) logAction(`Retention: ${label}`, 'retention_reviews', r.source_id,
      `${r.record_category}: ${(r.reference_label || '').slice(0, 60)}`, { status, source_table: r.source_table })
    load()
  }

  if (isAuditor) {
    return (
      <div>
        <div className="page-header"><h1>Records Retention</h1></div>
        <div className="notice notice-info" style={{ fontSize: 13 }}>This page is not available for the Auditor role.</div>
      </div>
    )
  }

  const eligible = rows.filter(r => r.base_status === 'Eligible for Review')
  const due90 = rows.filter(r => r.base_status === 'Due within 90 days')
  const shown = filter === 'eligible' ? eligible : filter === 'due90' ? due90 : rows

  return (
    <div>
      <div className="page-header"><h1>Records Retention</h1></div>

      <div className="notice notice-info" style={{ fontSize: 13, marginBottom: 14 }}>
        Records are retained for their defined period (default 5 years, configurable per category). Items eligible for
        disposal are reviewed here by Admin / Quality Manager and the decision is recorded with a reason. <strong>Nothing is
        deleted automatically</strong> — immutable evidence-chain records (report snapshots, report approvals,
        report endorsements, audit trail) are monitor-only and retained permanently.
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {[['all', `Eligible + due 90d (${rows.length})`], ['eligible', `Eligible now (${eligible.length})`], ['due90', `Due within 90 days (${due90.length})`]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ padding: '5px 12px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
            border: '1px solid var(--border)', fontWeight: filter === v ? 600 : 400,
            background: filter === v ? 'var(--brand)' : 'var(--bg)', color: filter === v ? '#fff' : 'var(--text2)' }}>{l}</button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Category</th><th>Reference</th><th>Retention start</th><th>Retention until</th><th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && shown.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No records eligible or due within 90 days.</td></tr>}
              {shown.map(r => {
                const actions = r.immutable ? ACTIONS_IMMUTABLE : ACTIONS_STD
                return (
                  <tr key={`${r.source_table}:${r.source_id}`}>
                    <td style={{ fontSize: 13 }}>{r.record_category}{r.immutable ? <span style={{ marginLeft: 6, fontSize: 13, color: 'var(--text3)' }} title="Immutable evidence — monitor only">🔒</span> : null}</td>
                    <td style={{ fontSize: 13, maxWidth: 300 }}>{r.reference_label || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.retention_start_date)}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.retention_until)}</td>
                    <td>
                      <span className={`badge ${statusBadge(statusOf(r))}`}>{statusOf(r)}</span>
                      {r.reviewed_by && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{r.reviewed_by} · {fmt(r.reviewed_at)}{r.reason ? ` — ${r.reason}` : ''}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {canApprove
                        ? actions.map(([status, label]) => (
                            <button key={status} className="sm" style={{ marginRight: 4 }} disabled={busy} onClick={() => record(r, status, label)}>{label}</button>
                          ))
                        : <span style={{ fontSize: 13, color: 'var(--text3)' }}>View only</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
